import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus, Trash2, Pencil, Database, Copy, Check, Zap, Star, Building2, ArrowLeftRight,
  KeyRound, Eye, EyeOff, FlaskConical,
} from "lucide-react";
import api, { formatApiError, APP_ORIGIN } from "@/lib/apiClient";
import BusinessModelPanel from "./BusinessModelPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const DB_TYPES = [
  { value: "mysql", label: "MySQL", port: 3306 },
  { value: "postgresql", label: "PostgreSQL", port: 5432 },
  { value: "mongodb", label: "MongoDB (internal)", port: 27017 },
  { value: "mongodb_external", label: "MongoDB (eksternal)", port: 27017 },
];

const STATUS = {
  connected: { cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", txt: "Terhubung" },
  failed: { cls: "bg-destructive/10 text-destructive border-destructive/20", txt: "Gagal" },
  not_configured: { cls: "bg-muted text-muted-foreground border-border", txt: "Belum diatur" },
};

const KEY_PROVIDERS = ["openai", "gemini"];
const KEY_LABEL = { openai: "OpenAI", gemini: "Gemini" };

function ApiKeysPanel({ business, onSaved }) {
  const [keys, setKeys] = useState({ openai: "", gemini: "" });
  const [show, setShow] = useState({ openai: false, gemini: false });
  const [busy, setBusy] = useState({});
  const [has, setHas] = useState({
    openai: !!business.has_openai_api_key,
    gemini: !!business.has_gemini_api_key,
  });

  useEffect(() => {
    setKeys({ openai: "", gemini: "" });
    setShow({ openai: false, gemini: false });
    setBusy({});
    setHas({ openai: !!business.has_openai_api_key, gemini: !!business.has_gemini_api_key });
  }, [business.id, business.has_openai_api_key, business.has_gemini_api_key]);

  const setBusyFor = (p, v) => setBusy((b) => ({ ...b, [p]: v }));

  const save = async (provider) => {
    if (!keys[provider].trim()) { toast.error("Masukkan API key terlebih dahulu"); return; }
    setBusyFor(provider, "save");
    try {
      await api.put(`/businesses/${business.id}/api-key`, { provider, api_key: keys[provider].trim() });
      setHas((h) => ({ ...h, [provider]: true }));
      setKeys((k) => ({ ...k, [provider]: "" }));
      toast.success(`${KEY_LABEL[provider]} API key bisnis tersimpan (terenkripsi)`);
      onSaved && await onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyFor(provider, ""); }
  };

  const clear = async (provider) => {
    setBusyFor(provider, "clear");
    try {
      await api.put(`/businesses/${business.id}/api-key`, { provider, clear: true });
      setHas((h) => ({ ...h, [provider]: false }));
      setKeys((k) => ({ ...k, [provider]: "" }));
      toast.success(`${KEY_LABEL[provider]} API key bisnis dihapus`);
      onSaved && await onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyFor(provider, ""); }
  };

  const test = async (provider) => {
    setBusyFor(provider, "test");
    try {
      const { data } = await api.post("/ai/test-key", { api_key: keys[provider].trim(), provider, business_id: business.id });
      if (data.ok) toast.success(data.message); else toast.error(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyFor(provider, ""); }
  };

  return (
    <div className="space-y-3 pt-2">
      <BusinessModelPanel business={business} onSaved={onSaved} />
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Isi API key sesuai model AI yang dipilih untuk bisnis ini: model <b>OpenAI</b> memakai OpenAI API key, model
        <b> Gemini</b> memakai Gemini API key. Key disimpan terenkripsi dan tidak pernah ditampilkan kembali.
      </p>
      {KEY_PROVIDERS.map((provider) => (
        <div key={provider} className="space-y-3 rounded-xl border border-border bg-muted/30 p-4"
          data-testid={`business-api-key-${provider}`}>
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-accent" />
            <h4 className="font-display font-semibold text-sm">{KEY_LABEL[provider]} API Key</h4>
          </div>
          <p className="text-xs" data-testid={`api-key-status-${provider}`}>
            Status: {has[provider]
              ? <span className="text-emerald-600 font-medium">Sudah diisi (terenkripsi)</span>
              : <span className="text-muted-foreground">Belum diisi</span>}
          </p>
          <div className="relative">
            <Input data-testid={`input-api-key-${provider}`} type={show[provider] ? "text" : "password"}
              value={keys[provider]}
              placeholder={has[provider] ? "Isi untuk mengganti key tersimpan..." : (provider === "openai" ? "sk-..." : "AIza...")}
              onChange={(e) => setKeys((k) => ({ ...k, [provider]: e.target.value }))}
              className="pr-10 font-mono text-xs" autoComplete="off" />
            <button type="button" data-testid={`toggle-show-api-key-${provider}`}
              onClick={() => setShow((s) => ({ ...s, [provider]: !s[provider] }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {show[provider] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" data-testid={`btn-test-api-key-${provider}`}
              onClick={() => test(provider)} disabled={!!busy[provider]}>
              <FlaskConical className="w-3.5 h-3.5 mr-1" /> {busy[provider] === "test" ? "Menguji..." : "Test API Key"}
            </Button>
            <Button type="button" size="sm" data-testid={`btn-save-api-key-${provider}`}
              onClick={() => save(provider)} disabled={!!busy[provider]}
              className="bg-accent hover:bg-accent/90 text-white">
              {busy[provider] === "save" ? "Menyimpan..." : "Simpan Key"}
            </Button>
            {has[provider] && (
              <Button type="button" variant="ghost" size="sm" data-testid={`btn-clear-api-key-${provider}`}
                onClick={() => clear(provider)} disabled={!!busy[provider]}
                className="text-destructive hover:text-destructive">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function EditDialog({ business, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(business.name);
  const [position, setPosition] = useState(business.config.position || "bottom-right");
  const [db, setDb] = useState({ ...business.db_config, password: "" });
  const [testing, setTesting] = useState(false);
  const [copied, setCopied] = useState(false);

  const token = business.token;
  const embed = `<!-- Veloce AI Sales Assistant -->\n<script src="${APP_ORIGIN}/api/embed/${token}.js" async></script>`;

  const saveGeneral = async () => {
    await api.put(`/businesses/${business.id}`, {
      name, currency: business.currency, language: business.language,
      config: { ...business.config, position },
    });
    toast.success("Info bisnis tersimpan");
    await onSaved();
  };

  const saveDb = async () => {
    await api.put(`/businesses/${business.id}/db-config`, {
      type: db.type, host: db.host || "", port: db.port || 3306,
      db_name: db.db_name || "", username: db.username || "",
      password: db.password || "", ssl: db.ssl ?? true, uri: db.uri || "",
      woocommerce_mode: db.woocommerce_mode ?? false, auth_source: db.auth_source || "",
    });
    return true;
  };

  const testDb = async () => {
    setTesting(true);
    try {
      await saveDb();
      const { data } = await api.post(`/businesses/${business.id}/test-db-connection`);
      if (data.status === "connected") toast.success(data.message);
      else toast.error(data.message);
      const fresh = await api.get(`/businesses/${business.id}`);
      setDb({ ...fresh.data.db_config, password: "" });
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(embed);
    setCopied(true);
    toast.success("Embed code disalin");
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="btn-edit-business"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100%_-_2rem)] max-w-xl grid-cols-1 max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="min-w-0 pr-6 text-left"><DialogTitle className="break-words leading-snug">Kelola — {business.name}</DialogTitle>
          <DialogDescription>Atur info, model AI, koneksi database, dan embed code untuk bisnis ini.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="general" className="min-w-0 w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general" className="min-w-0 px-1 text-xs sm:px-3 sm:text-sm" data-testid="tab-biz-general">Umum</TabsTrigger>
            <TabsTrigger value="database" className="min-w-0 px-1 text-xs sm:px-3 sm:text-sm" data-testid="tab-biz-database">Database</TabsTrigger>
            <TabsTrigger value="ai" className="min-w-0 px-1 text-xs sm:px-3 sm:text-sm" data-testid="tab-biz-ai">AI</TabsTrigger>
            <TabsTrigger value="embed" className="min-w-0 px-1 text-xs sm:px-3 sm:text-sm" data-testid="tab-biz-embed">Embed</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Nama Bisnis</Label>
              <Input data-testid="edit-business-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Posisi Icon Chatbot</Label>
              <div className="grid grid-cols-2 gap-2">
                {[{ v: "bottom-left", l: "Kiri Bawah", a: "justify-start" }, { v: "bottom-right", l: "Kanan Bawah", a: "justify-end" }].map((o) => (
                  <button key={o.v} type="button" data-testid={`edit-position-${o.v}`} onClick={() => setPosition(o.v)}
                    className={`rounded-xl border p-3 text-sm font-medium transition-colors ${position === o.v ? "border-accent bg-accent/10 text-accent" : "border-border hover:bg-secondary"}`}>
                    <div className="h-8 rounded-md bg-muted mb-2 flex items-end px-1 pb-1">
                      <div className={`w-full flex ${o.a}`}><span className="w-3 h-3 rounded-full" style={{ background: business.config.accent_color }} /></div>
                    </div>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={saveGeneral} data-testid="save-biz-general" className="bg-accent hover:bg-accent/90 text-white w-full">Simpan Info</Button>
          </TabsContent>

          <TabsContent value="database" className="space-y-3 pt-2">
            <div className="space-y-2">
              <Label>Jenis Database</Label>
              <Select value={db.type} onValueChange={(v) => { const t = DB_TYPES.find((d) => d.value === v); setDb((c) => ({ ...c, type: v, port: t.port })); }}>
                <SelectTrigger data-testid="edit-db-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DB_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Host</Label><Input data-testid="edit-db-host" value={db.host || ""} onChange={(e) => setDb((c) => ({ ...c, host: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Port</Label><Input type="number" value={db.port || ""} onChange={(e) => setDb((c) => ({ ...c, port: Number(e.target.value) }))} /></div>
              <div className="space-y-2"><Label>Nama Database</Label><Input data-testid="edit-db-name" value={db.db_name || ""} onChange={(e) => setDb((c) => ({ ...c, db_name: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Username</Label><Input value={db.username || ""} onChange={(e) => setDb((c) => ({ ...c, username: e.target.value }))} /></div>
            </div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" data-testid="edit-db-password" value={db.password || ""} placeholder={db.type === "mongodb" ? "(tidak diperlukan)" : "••••••••"} onChange={(e) => setDb((c) => ({ ...c, password: e.target.value }))} /></div>
            <Button onClick={testDb} disabled={testing} data-testid="edit-test-db" className="bg-accent hover:bg-accent/90 text-white w-full">
              <Zap className="w-4 h-4 mr-1.5" /> {testing ? "Menguji..." : "Test & Simpan Koneksi"}
            </Button>
          </TabsContent>

          <TabsContent value="ai" className="pt-2">
            <ApiKeysPanel business={business} onSaved={onSaved} />
          </TabsContent>

          <TabsContent value="embed" className="min-w-0 space-y-3 pt-2">
            <div className="flex items-center justify-between gap-3">
              <Label>Embed Code Unik</Label>
              <Button size="sm" variant="outline" onClick={copy} data-testid="edit-copy-embed" className="h-8 shrink-0 gap-1.5 px-3">
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Tersalin" : "Salin"}
              </Button>
            </div>
            <pre data-testid="edit-embed-code" className="m-0 w-full min-w-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] rounded-xl bg-primary p-3 font-mono text-[11px] leading-relaxed text-primary-foreground sm:p-4 sm:text-xs">{embed}</pre>
            <p className="text-xs leading-relaxed text-muted-foreground">Pasang sebelum &lt;/body&gt; di website bisnis ini. Chatbot otomatis membaca database & posisi ({business.config.position === "bottom-left" ? "kiri bawah" : "kanan bawah"}).</p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function TransferPanel({ reload }) {
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [targets, setTargets] = useState({});

  const load = async () => {
    const [all, adm] = await Promise.all([api.get("/admin/all-businesses"), api.get("/admins")]);
    setRows(all.data);
    setAdmins(adm.data);
  };
  useEffect(() => { load(); }, []);

  const transfer = async (biz) => {
    const newOwner = targets[biz.id];
    if (!newOwner) { toast.error("Pilih admin tujuan"); return; }
    try {
      await api.post(`/businesses/${biz.id}/transfer`, { new_owner_id: newOwner });
      toast.success(`${biz.name} dipindahkan`);
      await load();
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="transfer-panel">
      <div className="flex items-center gap-2 mb-1">
        <ArrowLeftRight className="w-4 h-4 text-accent" />
        <h3 className="font-display font-semibold">Transfer Kepemilikan Bisnis</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Pindahkan kepemilikan bisnis antar akun admin (khusus admin utama).</p>
      <div className="space-y-2">
        {rows.map((b) => (
          <div key={b.id} className="flex items-center gap-3 rounded-xl border border-border p-3 flex-wrap" data-testid="transfer-row">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm truncate">{b.name}</p>
              <p className="text-xs text-muted-foreground truncate">Pemilik: {b.owner_email}</p>
            </div>
            <div className="w-52">
              <Select value={targets[b.id] || ""} onValueChange={(v) => setTargets((t) => ({ ...t, [b.id]: v }))}>
                <SelectTrigger data-testid="transfer-target-select"><SelectValue placeholder="Pindah ke admin..." /></SelectTrigger>
                <SelectContent>
                  {admins.filter((a) => a.id !== b.owner_id).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="outline" data-testid="transfer-submit" onClick={() => transfer(b)}>
              <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Transfer
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BusinessManager({ businesses, reload, onSelect, currentUser }) {
  const [newName, setNewName] = useState("");
  const [open, setOpen] = useState(false);

  const create = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/businesses", { name: newName.trim() });
      setNewName(""); setOpen(false);
      await reload();
      toast.success("Bisnis ditambahkan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/businesses/${b.id}`);
      await reload();
      toast.success("Bisnis dihapus");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="manager-add-business" className="bg-accent hover:bg-accent/90 text-white"><Plus className="w-4 h-4 mr-1" /> Tambah Bisnis</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Tambah Bisnis Baru</DialogTitle></DialogHeader>
            <div className="space-y-2"><Label>Nama Bisnis</Label>
              <Input data-testid="manager-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="mis. Kopi Nusantara" /></div>
            <DialogFooter><Button onClick={create} data-testid="manager-create-submit" className="bg-accent text-white">Buat</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {businesses.map((b, i) => {
          const S = STATUS[b.db_config?.status] || STATUS.not_configured;
          return (
            <motion.div key={b.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-4" data-testid="manager-business-card">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: b.config.accent_color + "1a" }}>
                    <Building2 className="w-5 h-5" style={{ color: b.config.accent_color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display font-semibold truncate">{b.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{b.config.chatbot_name}</p>
                  </div>
                </div>
                {b.is_default && (
                  <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20 shrink-0" data-testid="default-badge">
                    <Star className="w-3 h-3 mr-1" /> Default
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap text-xs">
                <Badge variant="outline" className="font-mono uppercase"><Database className="w-3 h-3 mr-1" />{b.db_config?.type}</Badge>
                <Badge variant="outline" className={S.cls}>{S.txt}</Badge>
                <Badge variant="outline">{b.config.position === "bottom-left" ? "Kiri Bawah" : "Kanan Bawah"}</Badge>
              </div>

              <div className="flex items-center gap-2 mt-auto pt-2">
                <Button size="sm" variant="secondary" onClick={() => onSelect(b.id)} data-testid="manager-open-business">Buka</Button>
                <EditDialog business={b} onSaved={reload} />
                <Button size="icon" variant="ghost" className="ml-auto" data-testid="manager-delete-business"
                  disabled={b.is_default} onClick={() => remove(b)}>
                  <Trash2 className={`w-4 h-4 ${b.is_default ? "opacity-30" : "text-muted-foreground hover:text-destructive"}`} />
                </Button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {currentUser?.is_primary && <TransferPanel reload={reload} />}
    </div>
  );
}
